import dayjs from "dayjs";

import { findAndSetColumnSortOrder, TextCell, shortMonthDayAndYear } from "@similarweb/pro-logic-components";
import type { ColumnsType } from "@similarweb/pro-logic-components";

import { getIndexCellPropsForTableV2 } from "app/components/React/Table/cells/IndexCell";
import { translate } from "app/filters/i18nFilter";
import { numberFilter } from "app/filters/numberFilter";

import type { IIndustryCampaignRow } from "../../common/overview/industry/top-campaigns/useIndustryTopCampaigns";
import {
    renderAdvertiserCell,
    renderCampaignNameCell,
    renderLandingPageLinkCell,
} from "../../common/utils/campaignColumnCells";
import type { ISortProps } from "../../common/utils/utils";

export const getColumns = (
    totalRecords: number,
    currentPage: number,
    pageSize: number,
    sort: ISortProps,
    onAdvertiserClick: (domain: string) => void,
): ColumnsType<IIndustryCampaignRow> =>
    (
        [
            {
                ...getIndexCellPropsForTableV2<IIndustryCampaignRow>(currentPage, pageSize, undefined, 60),
            },
            {
                title: translate("ad.intelligence.advertiser.campaigns.campaigns.column.title", {
                    totalRecords: numberFilter()(totalRecords),
                }),
                tooltipTitle: translate("ad.intelligence.advertiser.campaigns.campaigns.column.tooltip.title"),
                tooltipSubtitle: translate("ad.intelligence.advertiser.campaigns.campaigns.column.tooltip"),
                dataIndex: "campaignName",
                width: 220,
                fixed: "left",
                render: renderCampaignNameCell,
            },
            {
                title: translate("ad.intelligence.advertiser.campaigns.landingPageLink.column.title"),
                tooltipTitle: translate("ad.intelligence.advertiser.campaigns.landingPageLink.column.tooltip.title"),
                tooltipSubtitle: translate("ad.intelligence.advertiser.campaigns.landingPageLink.column.tooltip"),
                dataIndex: "landingPageLink",
                width: 160,
                render: renderLandingPageLinkCell,
            },
            {
                title: translate("ad.intelligence.ads.table.advertiser.title"),
                tooltipSubtitle: translate("ad.intelligence.ads.table.advertiser.title.tooltip"),
                dataIndex: "advertiser",
                width: 160,
                render: (advertiser: string) => renderAdvertiserCell(advertiser, onAdvertiserClick),
            },
            {
                title: translate("ad.intelligence.advertiser.campaigns.creatives.column.title"),
                tooltipTitle: translate("ad.intelligence.advertiser.campaigns.creatives.column.tooltip.title"),
                tooltipSubtitle: translate("ad.intelligence.advertiser.campaigns.creatives.column.tooltip"),
                dataIndex: "creativesCount",
                width: 90,
                align: "right",
                sorter: true,
                render: (creativesCount: number) => <TextCell>{numberFilter()(creativesCount)}</TextCell>,
            },
            {
                title: translate("ad.intelligence.advertiser.campaigns.active.days.column.title"),
                tooltipTitle: translate("ad.intelligence.advertiser.campaigns.active.days.column.tooltip.title"),
                tooltipSubtitle: translate("ad.intelligence.advertiser.campaigns.active.days.column.tooltip"),
                dataIndex: "activeDays",
                width: 96,
                align: "left",
                render: (activeDays: number) =>
                    `${numberFilter()(activeDays)} ${translate(
                        activeDays > 1
                            ? "ad.intelligence.advertiser.campaigns.active.days.column.days"
                            : "ad.intelligence.advertiser.campaigns.active.days.column.day",
                    )}`,
            },
            {
                title: translate("ad.intelligence.advertiser.campaigns.first.seen.column.title"),
                tooltipTitle: translate("ad.intelligence.advertiser.campaigns.first.seen.column.tooltip.title"),
                tooltipSubtitle: translate("ad.intelligence.advertiser.campaigns.first.seen.column.tooltip"),
                dataIndex: "firstSeen",
                width: 120,
                align: "left",
                sorter: true,
                render: (firstSeen: string) => dayjs(firstSeen).format(shortMonthDayAndYear),
            },
            {
                title: translate("ad.intelligence.advertiser.campaigns.last.seen.column.title"),
                tooltipTitle: translate("ad.intelligence.advertiser.campaigns.last.seen.column.tooltip.title"),
                tooltipSubtitle: translate("ad.intelligence.advertiser.campaigns.last.seen.column.tooltip"),
                dataIndex: "lastSeen",
                width: 120,
                align: "left",
                sorter: true,
                render: (lastSeen: string) => dayjs(lastSeen).format(shortMonthDayAndYear),
            },
        ] as ColumnsType<IIndustryCampaignRow>
    ).map((col) => findAndSetColumnSortOrder(col, sort.sort, sort.asc));
